import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import CampaignIcon from "@mui/icons-material/Campaign";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { motion, AnimatePresence } from "framer-motion";
import api from "../axiosConfig";

const RANGE_OPTIONS = [
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "365", label: "Last 12 months" },
  { value: "all", label: "All time" },
];

const TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "inquiry", label: "Inquiries" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "call", label: "Calls" },
  { value: "campaign", label: "Campaigns" },
  { value: "task", label: "Tasks" },
  { value: "status", label: "Availability" },
  { value: "interaction", label: "Interactions" },
];

const typeStyles = {
  inquiry: { color: "#d97706", bg: "#fef3c7", icon: <PersonAddAlt1Icon fontSize="inherit" />, label: "Inquiry" },
  email: { color: "#2563eb", bg: "#dbeafe", icon: <MailOutlineIcon fontSize="inherit" />, label: "Email" },
  whatsapp: { color: "#16a34a", bg: "#dcfce7", icon: <WhatsAppIcon fontSize="inherit" />, label: "WhatsApp" },
  call: { color: "#0ea5e9", bg: "#e0f2fe", icon: <PhoneInTalkIcon fontSize="inherit" />, label: "Call" },
  campaign: { color: "#ea580c", bg: "#ffedd5", icon: <CampaignIcon fontSize="inherit" />, label: "Campaign" },
  task: { color: "#7c3aed", bg: "#ede9fe", icon: <TaskAltIcon fontSize="inherit" />, label: "Task" },
  status: { color: "#64748b", bg: "#e2e8f0", icon: <AutorenewIcon fontSize="inherit" />, label: "Status" },
  interaction: { color: "#0f766e", bg: "#ccfbf1", icon: <QueryStatsIcon fontSize="inherit" />, label: "Interaction" },
};

const formatTimeOnly = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDayBucket = (iso) => {
  if (!iso) return "Older";

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Older";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date >= today) return "Today";
  if (date >= yesterday) return "Yesterday";

  return date.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (iso) => {
  if (!iso) return "Unknown";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown";

  return date.toLocaleString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const resolveStyle = (type) => typeStyles[type] || typeStyles.interaction;

export default function ActivityTimeline() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [actorFilter, setActorFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const loadTimeline = useCallback(async (requestedRange) => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/analytics/activity-timeline", {
        params: {
          range: requestedRange,
          limit: requestedRange === "all" ? 500 : 250,
        },
      });

      const data = response.data?.data || [];
      setEvents(Array.isArray(data) ? data : []);
    } catch (requestError) {
      console.error("Failed to load activity timeline", requestError);
      setError("Could not load the activity timeline right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTimeline(range);
  }, [range, loadTimeline]);

  const actorOptions = useMemo(() => {
    const names = new Set();
    events.forEach((event) => {
      (event.actors || []).forEach((actor) => {
        if (actor?.name) {
          names.add(actor.name);
        }
      });
    });

    return ["all", ...Array.from(names).sort((a, b) => a.localeCompare(b))];
  }, [events]);

  const filteredEvents = useMemo(() => {
    let next = [...events];

    if (typeFilter !== "all") {
      next = next.filter((event) => event.type === typeFilter);
    }

    if (actorFilter !== "all") {
      next = next.filter((event) =>
        (event.actors || []).some((actor) => actor?.name === actorFilter)
      );
    }

    if (search.trim() !== "") {
      const term = search.trim().toLowerCase();
      next = next.filter((event) =>
        [event.title, event.subtitle, ...(event.actors || []).map((actor) => actor?.name || "")]
          .join(" ")
          .toLowerCase()
          .includes(term)
      );
    }

    if (dateFrom) {
      const start = new Date(dateFrom);
      next = next.filter((event) => new Date(event.timestamp) >= start);
    }

    if (dateTo) {
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      next = next.filter((event) => new Date(event.timestamp) <= end);
    }

    return next;
  }, [actorFilter, dateFrom, dateTo, events, search, typeFilter]);

  const groupedEvents = useMemo(() => {
    return filteredEvents.reduce((groups, event) => {
      const bucket = formatDayBucket(event.timestamp);
      if (!groups[bucket]) {
        groups[bucket] = [];
      }
      groups[bucket].push(event);
      return groups;
    }, {});
  }, [filteredEvents]);

  const summary = useMemo(() => {
    return filteredEvents.reduce(
      (totals, event) => {
        totals.total += 1;
        if (event.type === "email") totals.email += 1;
        if (event.type === "whatsapp") totals.whatsapp += 1;
        if (event.type === "call") totals.calls += 1;
        if (event.type === "campaign") totals.campaigns += 1;
        if (event.type === "inquiry") totals.inquiries += 1;
        return totals;
      },
      { total: 0, email: 0, whatsapp: 0, calls: 0, campaigns: 0, inquiries: 0 }
    );
  }, [filteredEvents]);

  return (
    <Paper sx={{ p: 0, bgcolor: "transparent", minHeight: 540 }}>
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 4,
          mb: 2.5,
          background: "linear-gradient(135deg,#ffffff,#f8fafc)",
        }}
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                All-Time Timeline
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Review the full operational history across inquiries, omnichannel actions, email campaigns, WhatsApp broadcasts, and contact interactions.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Range</InputLabel>
                <Select value={range} label="Range" onChange={(event) => setRange(event.target.value)}>
                  {RANGE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => loadTimeline(range)}
                disabled={loading}
                sx={{ borderRadius: 999 }}
              >
                Refresh
              </Button>

              <Button
                variant="text"
                startIcon={showFilters ? <CloseIcon /> : <FilterListIcon />}
                onClick={() => setShowFilters((open) => !open)}
                sx={{ borderRadius: 999 }}
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${summary.total} events`} sx={{ bgcolor: "#ecfdf3", color: "#15803d", fontWeight: 700 }} />
            <Chip label={`${summary.inquiries} inquiries`} sx={{ bgcolor: "#fef3c7", color: "#b45309" }} />
            <Chip label={`${summary.email} email`} sx={{ bgcolor: "#dbeafe", color: "#1d4ed8" }} />
            <Chip label={`${summary.whatsapp} WhatsApp`} sx={{ bgcolor: "#dcfce7", color: "#15803d" }} />
            <Chip label={`${summary.calls} calls`} sx={{ bgcolor: "#e0f2fe", color: "#0369a1" }} />
            <Chip label={`${summary.campaigns} campaigns`} sx={{ bgcolor: "#ffedd5", color: "#c2410c" }} />
          </Stack>

          <Collapse in={showFilters}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={typeFilter} label="Type" onChange={(event) => setTypeFilter(event.target.value)}>
                    {TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Actor</InputLabel>
                  <Select value={actorFilter} label="Actor" onChange={(event) => setActorFilter(event.target.value)}>
                    {actorOptions.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name === "all" ? "All actors" : name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  fullWidth
                  label="Search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  value={dateFrom}
                  onChange={(event) => setDateFrom(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  value={dateTo}
                  onChange={(event) => setDateTo(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setTypeFilter("all");
                    setActorFilter("all");
                    setSearch("");
                    setDateFrom("");
                    setDateTo("");
                  }}
                  sx={{ minWidth: 150, borderRadius: 999 }}
                >
                  Reset Filters
                </Button>
              </Stack>
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      {loading ? (
        <Stack spacing={1.5}>
          {[1, 2, 3].map((row) => (
            <Box key={row} sx={{ display: "flex", gap: 2 }}>
              <Skeleton variant="circular" width={42} height={42} />
              <Skeleton variant="rounded" height={92} sx={{ flex: 1 }} />
            </Box>
          ))}
        </Stack>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : filteredEvents.length === 0 ? (
        <Alert severity="info">No activity matched the current timeline filters.</Alert>
      ) : (
        <Box sx={{ position: "relative", px: { xs: 0, md: 1 } }}>
          <Box
            sx={{
              position: "absolute",
              left: 21,
              top: 0,
              bottom: 12,
              borderLeft: "2px dashed #dbe4ee",
              zIndex: 0,
            }}
          />

          <Stack spacing={1}>
            {Object.entries(groupedEvents).map(([bucket, bucketEvents]) => (
              <Box key={bucket} sx={{ position: "relative", zIndex: 1 }}>
                <Chip
                  label={bucket}
                  size="small"
                  sx={{
                    ml: 6.5,
                    mb: 1.5,
                    bgcolor: "rgba(255,255,255,0.95)",
                    border: "1px solid rgba(148,163,184,0.25)",
                    fontWeight: 700,
                  }}
                />

                <AnimatePresence initial={false}>
                  {bucketEvents.map((event) => {
                    const style = resolveStyle(event.type);
                    const primaryActor = event.actors?.[0]?.name || "System";

                    return (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Box sx={{ display: "flex", gap: 2, mb: 2.25 }}>
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              minWidth: 42,
                              borderRadius: "15px",
                              bgcolor: style.color,
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: `0 6px 16px ${style.color}55`,
                              border: "3px solid #f8fafc",
                            }}
                          >
                            {style.icon}
                          </Box>

                          <Paper
                            sx={{
                              flex: 1,
                              p: 2,
                              borderRadius: 4,
                              background: "linear-gradient(180deg,#ffffff,#f8fafc)",
                              border: "1px solid rgba(148,163,184,0.18)",
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                  {event.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                                  {event.subtitle || "Operational activity"}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                                {formatTimeOnly(event.timestamp)}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                              <Chip
                                label={style.label}
                                size="small"
                                sx={{
                                  bgcolor: style.bg,
                                  color: style.color,
                                  fontWeight: 700,
                                }}
                              />
                              <Chip
                                label={primaryActor}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: "rgba(148,163,184,0.35)" }}
                              />
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {formatDateTime(event.timestamp)}
                              </Typography>
                            </Stack>

                            {event.actors?.length ? (
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.4 }}>
                                {event.actors.slice(0, 4).map((actor, index) => (
                                  <Stack key={`${event.id}-${actor.name}-${index}`} direction="row" spacing={0.8} alignItems="center">
                                    <Avatar src={actor.src || undefined} sx={{ width: 24, height: 24, fontSize: 11 }}>
                                      {(actor.name || "?").charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                      {actor.name}
                                    </Typography>
                                  </Stack>
                                ))}
                              </Stack>
                            ) : null}
                          </Paper>
                        </Box>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
